// ============================================================================
// CECODEV Platform - Material-UI Theme Configuration
// ============================================================================

import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Couleurs CECODEV — SOURCE UNIQUE de la charte côté client (le pendant
// serveur pour les emails/PDF est `packages/server/src/utils/brand.ts` :
// ajuster les DEUX ensemble). Logo : hexagones coral + teal, texte gris.
export const cecodevColors = {
  coral: '#E1512C',      // Rouge-orangé principal (hexagone du logo)
  coralLight: '#E8735A',
  coralDark: '#C43D1A',
  teal: '#4BB7A5',       // Turquoise secondaire (hexagone du logo)
  tealLight: '#6DC9BA',
  tealDark: '#3A9A8A',
  // « Encre » : vert profond pour titres/accents sombres — remplace le marine
  // #1a1a2e jamais validé (décision FX 2026-08-29, valeur ajustable ici).
  encre: '#1E4A43',
  encreLight: '#2E6459',
  charcoal: '#3D3D3D',   // Texte foncé
  grey: '#6B7280',
  lightGrey: '#F7F8FA',
  fondDoux: '#F2FAF8',   // Fond très léger teinté teal (encarts, astuces)
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: cecodevColors.coral,
      light: cecodevColors.coralLight,
      dark: cecodevColors.coralDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: cecodevColors.teal,
      light: cecodevColors.tealLight,
      dark: cecodevColors.tealDark,
      contrastText: '#ffffff',
    },
    background: {
      default: cecodevColors.lightGrey,
      paper: '#ffffff',
    },
    success: {
      main: cecodevColors.teal,
      light: cecodevColors.tealLight,
      dark: cecodevColors.tealDark,
    },
    warning: {
      main: '#F5A623',
      light: '#FFBE4D',
      dark: '#D4890A',
    },
    error: {
      main: cecodevColors.coral,
      light: cecodevColors.coralLight,
      dark: cecodevColors.coralDark,
    },
    info: {
      main: cecodevColors.teal,
      light: cecodevColors.tealLight,
      dark: cecodevColors.tealDark,
    },
    text: {
      primary: cecodevColors.charcoal,
      secondary: cecodevColors.grey,
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.1rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(225, 81, 44, 0.3)',
          },
        },
        contained: {
          backgroundColor: cecodevColors.coral,
          '&:hover': {
            backgroundColor: cecodevColors.coralDark,
          },
        },
        containedSecondary: {
          backgroundColor: cecodevColors.teal,
          '&:hover': {
            backgroundColor: cecodevColors.tealDark,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

// Typographie dégressive sur petit écran (plan responsive R0.4) : les h1-h6
// ci-dessus sont les tailles DESKTOP, MUI les réduit automatiquement sous
// les breakpoints (h1 2.5rem ne s'affiche plus tel quel sur 375 px).
export default responsiveFontSizes(theme);

